import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page, StatTiles } from '../../components/shell/Page';
import SplitLayout, { paneForm } from '../../components/shell/SidePane';
import { useResetPasswordPane } from '../../components/shell/ResetPasswordPane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
  Plus,
  User,
  Users,
  Trash,
  PencilSimple,
  Key,
  ToggleRight,
  Upload,
  Download,
  Clock,
  Buildings,
  ClockCounterClockwise,
  SignIn
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  ['all', 'All'],
  ['super_admin', 'Super admin'],
  ['tenant_admin', 'Tenant admin'],
  ['instructor', 'Instructors'],
  ['learner', 'Learners'],
];

export default function SuperAdminUserManagement() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [userActivityLogs, setUserActivityLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');

  // Side pane states (edit / bulk import / activity). "New user" is a big form → its own tab (/super-admin/users/new)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [userActivityDialogOpen, setUserActivityDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [csvData, setCsvData] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, tenantsRes, statsRes, logsRes] = await Promise.all([
        api.get('/users'),
        api.get('/tenants'),
        api.get('/users/stats'),
        api.get('/activity-logs?limit=50')
      ]);
      setUsers(usersRes.data);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
      setActivityLogs(logsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // "New user" opens the shared form in its own tab (pages/admin/UserForm.js)
  const openCreateUser = () => navigate('/super-admin/users/new');

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await api.put(`/users/${selectedUser.id}`, {
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        role: selectedUser.role,
        tenant_id: selectedUser.tenant_id,
        is_active: selectedUser.is_active
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const res = await api.put(`/users/${userId}/status`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Reset password → side pane (set password with eye icon / Generate, "require change at first login"), no alert()
  const resetPane = useResetPasswordPane({ api, onReset: fetchData });

  const handleBulkImport = async () => {
    if (!csvData.trim()) {
      toast.error('Please enter CSV data');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/users/bulk-import', { csv_data: csvData });
      toast.success(`Imported ${res.data.created} users. ${res.data.failed.length} failed.`);
      if (res.data.failed.length > 0) {
        console.log('Failed imports:', res.data.failed);
      }
      setBulkImportDialogOpen(false);
      setCsvData('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewUserActivity = async (user) => {
    setSelectedUser(user);
    try {
      const res = await api.get(`/activity-logs?user_id=${user.id}&limit=50`);
      setUserActivityLogs(res.data);
    } catch (error) {
      setUserActivityLogs([]);
    }
    openPane(setUserActivityDialogOpen);
  };

  const handleLoginAsUser = async (user) => {
    try {
      const res = await api.post(`/users/${user.id}/impersonate`);
      const { token, user: targetUser, tenant } = res.data;

      // Build the impersonate URL with tenant info as query param
      let loginUrl = `${window.location.origin}/impersonate?token=${token}`;

      // Add tenant subdomain as query param for instructor/learner
      if (tenant?.subdomain && (targetUser.role === 'instructor' || targetUser.role === 'learner')) {
        loginUrl += `&tenant=${tenant.subdomain}`;
      }

      window.open(loginUrl, '_blank');

      toast.success(`Opened login session for ${targetUser.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesTenant = tenantFilter === 'all' || u.tenant_id === tenantFilter;
    return matchesRole && matchesTenant;
  });

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'Platform';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || 'Unknown';
  };

  const roleCount = (role) => (role === 'all' ? users.length : users.filter(u => u.role === role).length);

  const downloadCSVTemplate = () => {
    const template = 'email,first_name,last_name,role,password,tenant_id\nuser@example.com,John,Doe,learner,Welcome123!,tenant-id-here';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
  };

  // ─── Side pane (small CRUD forms / read-only log open here instead of popups) ───
  const closePane = () => { setEditDialogOpen(false); setBulkImportDialogOpen(false); setUserActivityDialogOpen(false); resetPane.close(); };
  const openPane = (which) => { closePane(); which(true); };
  const openEdit = (u) => { setSelectedUser({ ...u }); openPane(setEditDialogOpen); };
  const openReset = (u) => { setEditDialogOpen(false); setBulkImportDialogOpen(false); setUserActivityDialogOpen(false); resetPane.open(u); };
  let side = resetPane.side;
  if (editDialogOpen && selectedUser) {
    const f = paneForm({ id: 'edit-user-form', onSubmit: handleUpdateUser, onCancel: closePane, submitLabel: 'Save changes', submitting, children: (
      <>
      <div className="pane-grid">
        <div className="field">
          <Label>First Name</Label>
          <Input
            value={selectedUser.first_name}
            onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})}
            required
          />
        </div>
        <div className="field">
          <Label>Last Name</Label>
          <Input
            value={selectedUser.last_name}
            onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})}
            required
          />
        </div>
      </div>
      <div className="field">
        <Label>Email</Label>
        <Input value={selectedUser.email || ''} disabled />
      </div>
      <div className="pane-grid">
        <div className="field">
          <Label>Role</Label>
          <Select
            value={selectedUser.role}
            onValueChange={(v) => setSelectedUser({...selectedUser, role: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="learner">Learner</SelectItem>
              <SelectItem value="instructor">Instructor</SelectItem>
              <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="field">
          <Label>Tenant</Label>
          <Select
            value={selectedUser.tenant_id || '__none__'}
            onValueChange={(v) => setSelectedUser({...selectedUser, tenant_id: v === '__none__' ? null : v})}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select tenant" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Platform (No Tenant)</SelectItem>
              {tenants.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: `Edit: ${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim(), subtitle: 'Update user information', icon: PencilSimple, children: f.body, footer: f.footer, testid: 'edit-user-pane' };
  } else if (bulkImportDialogOpen) {
    const f = paneForm({ id: 'bulk-import-form', onSubmit: (e) => { e.preventDefault(); handleBulkImport(); }, onCancel: closePane, submitLabel: 'Import users', submitting, submitIcon: Upload, children: (
      <>
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={downloadCSVTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>
      </div>
      <div className="field">
        <Label>CSV Data</Label>
        <Textarea
          placeholder="email,first_name,last_name,role,password,tenant_id&#10;user@example.com,John,Doe,learner,Welcome123!,tenant-id"
          value={csvData}
          onChange={(e) => setCsvData(e.target.value)}
          rows={12}
          className="font-mono text-sm"
        />
        <p className="field-hint">
          Required columns: email, first_name, last_name, role. Optional: password (auto-generated if empty), tenant_id
        </p>
      </div>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'Bulk import users', subtitle: 'Import multiple users from CSV data', icon: Upload, width: 44, children: f.body, footer: f.footer, testid: 'bulk-import-pane' };
  } else if (userActivityDialogOpen) {
    side = {
      open: true, onOpenChange: closePane, title: 'User activity log', icon: ClockCounterClockwise, testid: 'user-activity-pane',
      subtitle: selectedUser && `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`,
      children: (
        <div className="space-y-3">
          {userActivityLogs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No activity logs for this user</p>
          ) : (
            userActivityLogs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-3 border rounded-lg">
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{log.action.replace(/_/g, ' ')}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground break-words">{log.details}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(log.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      ),
      footer: (
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={closePane} data-testid="pane-close">Close</Button>
        </div>
      ),
    };
  }

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
      <Page data-testid="super-admin-user-management">
        {stats && (
          <StatTiles items={[
            { label: 'Total users', value: stats.total_users, icon: Users },
            { label: 'Active users', value: stats.active_users, icon: User, iconClass: 'bg-emerald-100 text-emerald-700' },
            { label: 'Inactive users', value: stats.inactive_users, icon: User, iconClass: 'bg-red-100 text-red-700' },
            { label: 'Tenants', value: tenants.length, icon: Buildings, iconClass: 'bg-sky-100 text-sky-700' },
          ]} />
        )}

        <Tabs defaultValue="users" className="space-y-3">
          <TabsList>
            <TabsTrigger value="users">All Users</TabsTrigger>
            <TabsTrigger value="activity">Activity Logs</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <DataGrid
              title="Users"
              rows={filteredUsers}
              loading={loading}
              onRefresh={fetchData}
              searchKeys={['email', 'phone', 'first_name', 'last_name']}
              headerExtra={(
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="role-filter">
                  {ROLE_OPTIONS.map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setRoleFilter(v)}
                      className={`px-3 h-8 whitespace-nowrap ${roleFilter === v ? 'bg-shell-primary text-white font-semibold' : 'bg-white hover:bg-muted'}`}
                    >
                      {l} ({roleCount(v)})
                    </button>
                  ))}
                </div>
              )}
              advancedSearch={(
                <div className="flex flex-col md:flex-row md:items-end gap-3">
                  <div className="field">
                    <Label className="text-xs">Tenant</Label>
                    <Select value={tenantFilter} onValueChange={setTenantFilter}>
                      <SelectTrigger className="w-[220px]" data-testid="tenant-filter">
                        <SelectValue placeholder="Filter by tenant" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tenants</SelectItem>
                        {tenants.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {tenantFilter !== 'all' && (
                    <Button variant="ghost" size="sm" className="h-9" onClick={() => setTenantFilter('all')}>
                      Clear
                    </Button>
                  )}
                </div>
              )}
              activeFilterCount={tenantFilter !== 'all' ? 1 : 0}
              columns={[
                {
                  key: 'name', header: 'User', accessor: (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                  render: (u) => (
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-shell-primary/10 text-shell-primary flex items-center justify-center text-[11px] font-semibold">
                        {`${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase()}
                      </div>
                      <span className="font-medium">{u.first_name} {u.last_name}</span>
                    </div>
                  ),
                },
                { key: 'contact', header: 'Email / Phone', accessor: (u) => u.email || u.phone || '', className: 'text-muted-foreground' },
                { key: 'role', header: 'Role', width: 130, render: (u) => <StatusBadge value={u.role} /> },
                { key: 'tenant', header: 'Tenant', accessor: (u) => getTenantName(u.tenant_id) },
                { key: 'is_active', header: 'Status', width: 170, accessor: (u) => !!u.is_active, render: (u) => <span className="inline-flex items-center gap-1"><StatusBadge value={!!u.is_active} />{u.must_change_password && <StatusBadge value="Temp password" tone="warning" />}</span> },
                { key: 'created_at', header: 'Joined', width: 120, render: (u) => new Date(u.created_at).toLocaleDateString() },
              ]}
              rowTone={(u) => (u.is_active ? null : 'muted')}
              onRowDoubleClick={openEdit}
              primaryActions={(
                <>
                  <Button size="sm" className="h-9" onClick={openCreateUser} data-testid="add-user-btn">
                    <Plus className="w-4 h-4" /> New user
                  </Button>
                  <Button size="sm" variant="outline" className="h-9" onClick={() => openPane(setBulkImportDialogOpen)}>
                    <Upload className="w-4 h-4" /> Bulk import
                  </Button>
                </>
              )}
              selectionActions={[
                { key: 'edit', label: 'Edit', icon: PencilSimple, onClick: ([u]) => openEdit(u) },
                { key: 'impersonate', label: 'Impersonate', icon: SignIn, onClick: ([u]) => handleLoginAsUser(u) },
                { key: 'toggle', label: 'Activate / Deactivate', icon: ToggleRight, onClick: ([u]) => handleToggleStatus(u.id) },
                { key: 'reset', label: 'Reset password', icon: Key, onClick: ([u]) => openReset(u), disabled: (rows) => rows[0]?.role === 'super_admin' },
                { key: 'activity', label: 'Activity', icon: ClockCounterClockwise, onClick: ([u]) => handleViewUserActivity(u) },
                { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([u]) => handleDeleteUser(u.id) },
              ]}
              emptyMessage="No users found"
            />
          </TabsContent>

          <TabsContent value="activity">
            <DataGrid
              title="Recent activity"
              rows={activityLogs}
              loading={loading}
              onRefresh={fetchData}
              selectable={false}
              columns={[
                { key: 'timestamp', header: 'When', width: 180, accessor: (l) => l.timestamp || l.created_at, render: (l) => new Date(l.timestamp || l.created_at).toLocaleString() },
                { key: 'user_name', header: 'User', width: 180, accessor: (l) => l.user_name || 'Unknown', className: 'font-medium' },
                { key: 'action', header: 'Action', width: 160, render: (l) => <StatusBadge value={l.action} tone="neutral" /> },
                { key: 'details', header: 'Details', className: 'text-muted-foreground' },
              ]}
              emptyMessage="No activity logs yet"
            />
          </TabsContent>
        </Tabs>
      </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
